import { create } from 'zustand';
import { nanoid } from 'nanoid';
import { DEFAULT_SETTINGS } from '@/db';
import { getRepo, type Repo } from '@/data/repo';
import { linkDealToBitrix } from '@/data/syncClient';
import type {
  AppSettings, Branch, Deal, DealKind, DealStatus, Employee, GameFormat,
  ID, Occasion, RefusalReason, Source, Team, Department,
} from '@/types';
import { startOfDay as soDay } from '@/utils/time';
import {
  applyAttachmentBodies, collectAttachmentBodies, hasUnsavedAttachmentBody, pendingAttachmentKeys, type AttachmentBodies,
} from '@/utils/attachments';
import { isInBitrix, initBitrix, fetchCurrentUser, getBitrixAuth, getPlacementDealId } from '@/providers/bitrixClient';
import type { CurrentUser, RolePermissions } from '@/auth/permissions';
import { LOCAL_DEV_USER, perms } from '@/auth/permissions';
import { setUnauthorizedHandler, setToken, authHeaders } from '@/auth/session';
import type { RateRow, RatesResponse } from '@/utils/currency';
import {
  fetchMe, loginLocal, loginBitrix as apiLoginBitrix, logout as apiLogout, register as apiRegister,
  updateProfile as apiUpdateProfile,
  requestTelegramLink as apiRequestTelegramLink, unlinkTelegram as apiUnlinkTelegram,
} from '@/auth/authClient';

/** Активный репозиторий данных (api | local). Выбирается в init(). */
let repo: Repo;

/**
 * Курсы Нацбанка по дням. Справочник необязательный: 404 (роута ещё нет), офлайн или
 * кривой ответ — не ошибка, просто пустой список и работа как раньше.
 */
async function fetchCurrencyRates(): Promise<RateRow[]> {
  try {
    const res = await fetch('/api/currency/rates', { headers: { Accept: 'application/json', ...authHeaders() } });
    if (!res.ok) return [];
    const body = (await res.json()) as RatesResponse;
    return Array.isArray(body?.rates) ? body.rates : [];
  } catch {
    return [];
  }
}

/**
 * Метка текущей догрузки вложений. Растёт при открытии другой карточки и при её закрытии:
 * очередь сверяется с меткой после каждого файла и бросает хвост, если он уже никому не нужен
 * (пролистали на соседнюю игру, закрыли карточку). Так в полёте всегда не больше одного файла.
 */
let attachRun = 0;

/**
 * Следующий номер заказа — в том же виде, что у сделок из Битрикса (просто число, как «#26788»).
 * Номера из Битрикса — это ID сделки (большие, 100000+); ручные нумеруем своей сквозной
 * последовательностью в нижнем диапазоне (1, 2, 3…), чтобы не пересекаться с ними.
 */
function computeNextDealNumber(deals: Deal[], _kind: DealKind): string {
  const max = deals
    .map((d) => d.number)
    .filter((n): n is string => !!n && /^\d+$/.test(n))
    .map((n) => parseInt(n, 10))
    .filter((n) => Number.isFinite(n) && n < 100000)
    .reduce((m, n) => (n > m ? n : m), 0);
  return String(max + 1);
}

type ToastKind = 'info' | 'success' | 'warn' | 'error';
export interface Toast {
  id: string;
  text: string;
  kind: ToastKind;
}

export type Page = 'dashboard' | 'calendar' | 'crm' | 'forms' | 'reports' | 'fill_rate' | 'finance' | 'personnel' | 'directory'
  | 'branches' | 'profile' | 'access' | 'roles_matrix' | 'prebooking' | 'my_games' | 'pay_rates';

/** Какой часовой пояс показывают часы и используют экраны со временем. */
export type ClockTz = { kind: 'system' } | { kind: 'branch' } | { kind: 'offset'; min: number };

export type ModalKind =
  | { kind: 'deal'; id: ID | null; preset?: Partial<Deal> }
  | { kind: 'branch'; id: ID | null }
  | { kind: 'employee'; id: ID | null }
  | { kind: 'team'; id: ID | null }
  | { kind: 'help' }
  | { kind: 'confirm'; title: string; message: string; onConfirm: () => void; danger?: boolean };

interface State {
  // data caches
  branches: Branch[];
  employees: Employee[];
  teams: Team[];
  sources: Source[];
  occasions: Occasion[];
  game_formats: GameFormat[];
  refusal_reasons: RefusalReason[];
  deals: Deal[];
  /**
   * Тела вложений (фото/PDF в base64) по сделкам: id сделки → { ключ вложения → data_url }.
   * В списке сделок тел нет — карточка догружает их по одному (loadDealAttachments) и кладёт
   * сюда. Кэш переживает перезагрузку списка и ответ сервера на сохранение: оттуда тела
   * подставляются обратно в сделки, чтобы фото не мигали и не качались заново.
   */
  dealAttachments: Record<ID, AttachmentBodies>;
  settings: AppSettings;
  /**
   * Курсы валют Нацбанка по дням (сколько тенге за 1 единицу) — для пересчёта сумм сделок
   * не в тенге по курсу НА ДАТУ ИГРЫ. Грузятся один раз вместе со справочниками; если роут
   * недоступен — остаётся пустой список, и суммы показываются как раньше.
   */
  currencyRates: RateRow[];
  ready: boolean;
  /**
   * Идёт фоновая догрузка ВСЕХ сделок (фаза 2). На старте показываем только текущий месяц
   * (быстрый первый экран), а полный список доливаем в фоне — на это время флаг true,
   * UI может показать ненавязчивый индикатор «данные догружаются…».
   */
  dealsLoadingMore: boolean;

  // Авторизация
  currentUser: CurrentUser | null;
  authStatus: 'loading' | 'anonymous' | 'authed';
  authError: string | null;

  // Битрикс (встроенный режим)
  bitrixConnected: boolean;
  bitrixUser: string | null;
  bitrixError: string | null;
  /** Если приложение открыто из карточки сделки Битрикса — id нашей сделки ('bx<dealId>'). */
  bitrixDealId: ID | null;

  // ui
  page: Page;
  modal: ModalKind | null;
  /** Открытая полноэкранная карточка игры (Продажи → Игры): id сделки, 'new' для добавления, null — таблица. */
  salesDealId: ID | 'new' | null;
  /** Соседи открытой карточки: id сделок в том порядке, в котором они видны на экране,
   *  с которого карточку открыли. Нужен для листания стрелками «‹ ›». Пусто — листания нет. */
  dealNavIds: ID[];
  /** Активная вкладка раздела «Персонал» (для подменю Сотрудники/Группы). */
  personnelTab: 'employees' | 'teams';
  toasts: Toast[];
  /** Сколько сейчас идёт сохранений сделки С НОВЫМ ВЛОЖЕНИЕМ (0 = нет). Пока > 0 —
   *  показываем плашку «фото загружается, не закрывайте страницу» и предупреждаем
   *  перед закрытием/перезагрузкой (мобильный интернет — PUT идёт несколько секунд). */
  attachmentSaving: number;
  // фильтры
  filterBranchId: ID | 'all';
  filterTeamId: ID | 'all';
  // маркер на карточках (не прячет, а помечает значком): none/confirmed/finance
  markerFilter: 'none' | 'confirmed' | 'finance';
  filterDept: Department | 'all';
  filterStatus: DealStatus | 'all';
  search: string;
  /** Выбранный в часах пояс — влияет на показ времени (ось «Ресурсы» и т.п.). */
  clockTz: ClockTz;
  /** Форс-вид календаря при следующем заходе (напр. открыть сразу «Ресурсы»/Гант). */
  pendingCalView: string | null;
  /** Форс-день (начало дня, мс) для календаря при следующем заходе. */
  pendingCalDay: number | null;

  // actions
  init(): Promise<void>;
  reload(): Promise<void>;
  /** Подтянуть сотрудников из Битрикса (встроенный режим). */
  connectBitrix(): Promise<void>;

  // auth actions
  login(login: string, password: string): Promise<void>;
  /** Повторный вход через Битрикс (для кнопки на экране входа внутри портала). */
  loginViaBitrix(): Promise<void>;
  /** true → регистрация ушла на подтверждение (вход не выдан); false → вошли сразу. */
  register(token: string, login: string, password: string, fullName?: string, phone?: string, branchId?: string, workerType?: string): Promise<boolean>;
  logout(): Promise<void>;
  updateMyProfile(email: string | null, phone: string | null, login?: string): Promise<void>;
  updateMyAvatar(avatar: string | null): Promise<void>;
  /** Дата рождения ('YYYY-MM-DD' или null) — заполняет сам сотрудник в «Профиле». */
  updateMyBirthDate(birthDate: string | null): Promise<void>;
  /** Запросить ссылку привязки Telegram (deep-link на бота). */
  requestTelegramLink(): Promise<string>;
  /** Отвязать Telegram у текущего пользователя (и обновить статус в currentUser). */
  unlinkTelegram(): Promise<void>;
  /** Перезапросить /me — обновить currentUser (в т.ч. статус привязки Telegram). */
  refreshMe(): Promise<void>;
  /** Внутреннее: загрузить данные после успешной авторизации. */
  loadData(): Promise<void>;

  setPage(p: Page): void;
  /** navIds — список соседей для листания карточки (порядок как на экране); не передан — листания нет. */
  setModal(m: ModalKind | null, navIds?: ID[]): void;
  openSalesDeal(id: ID | 'new' | null, navIds?: ID[]): void;
  /** Листание по соседям: меняем только id открытой карточки, список соседей сохраняем. */
  navigateDeal(id: ID): void;
  setPersonnelTab(t: 'employees' | 'teams'): void;
  /** Определить, открыто ли приложение из карточки сделки (placement), и запомнить её. */
  detectBitrixPlacement(): Promise<void>;
  /** Выйти из режима «эта сделка» в общий интерфейс. */
  clearBitrixDeal(): void;
  toast(t: Omit<Toast, 'id'>): void;
  dismissToast(id: string): void;

  setFilterBranch(id: ID | 'all'): void;
  /** Задать форс-вид (и опц. день) для календаря; читается один раз при монтировании. */
  setPendingCal(view: string | null, day?: number | null): void;
  setMarkerFilter(m: 'none' | 'confirmed' | 'finance'): void;
  setFilterTeam(id: ID | 'all'): void;
  setFilterDept(d: Department | 'all'): void;
  setFilterStatus(s: DealStatus | 'all'): void;
  setSearch(s: string): void;
  setClockTz(tz: ClockTz): void;

  // CRUD
  patchSettings(p: Partial<AppSettings>): Promise<void>;
  saveBranch(b: Branch): Promise<void>;
  deleteBranch(id: ID): Promise<void>;
  saveEmployee(e: Employee): Promise<void>;
  deleteEmployee(id: ID): Promise<void>;
  saveTeam(t: Team): Promise<void>;
  deleteTeam(id: ID): Promise<void>;
  saveDeal(d: Deal): Promise<void>;
  /** Фоновая догрузка тел вложений открытой карточки: по одному, с кэшем. */
  loadDealAttachments(id: ID): Promise<void>;
  /** Карточку закрыли — бросить хвост незакончившейся догрузки. */
  cancelDealAttachments(): void;
  /** A9: связать ручное ERP-событие со сделкой Битрикса (по номеру сделки или телефону). */
  linkDeal(erpId: ID, opts: { bitrixId?: string; phone?: string }): Promise<{ ok: boolean; bitrixId?: string; reason?: string }>;
  newDeal(kind: DealKind, preset?: Partial<Deal>): Promise<Deal>;
  deleteDeal(id: ID): Promise<void>;
  duplicateDeal(id: ID): Promise<Deal | null>;
  /** Сдвигает дату мероприятия + start/end на newDayTs (день начала). branchId — опционально. */
  moveDealToDay(id: ID, newDayTs: number, branchId?: ID | null): Promise<void>;
  /** Убирает дату/время — сделка возвращается в «Очередь». */
  unscheduleDeal(id: ID): Promise<void>;
  /** Меняет статус с пометкой taken_at если нужно. */
  setDealStatus(id: ID, status: DealStatus): Promise<void>;
}

// Какое право открывает каждую страницу (для гейтинга и посадочной).
const PAGE_PERM: Record<Page, (p: RolePermissions) => boolean> = {
  dashboard: (p) => p.pages.dashboard,
  calendar: (p) => p.pages.calendar,
  crm: (p) => p.pages.sales,
  forms: (p) => p.pages.sales,
  reports: (p) => p.pages.reports,
  // «Заполняемость» — та же аудитория, что и «Отчёты» (руководитель/РОП). Отдельное право
  // не заводим специально: новое право пришлось бы раскатывать по ролям на боевом.
  fill_rate: (p) => p.pages.reports,
  finance: (p) => p.pages.dashboard,   // финансовая аналитика — управленцам
  // Раздел «Персонал» открыт и по узкому праву «Загрузка» (кассир): внутри ему видна
  // только вкладка «Загрузка», список сотрудников и бригады — нет (гейт в PersonnelPage).
  personnel: (p) => p.pages.personnel || p.pages.workload,
  // «Мои игры» — личный список отработанных игр. Отдельного права нет: экран доступен
  // любому, кто привязан к карточке сотрудника (проверка по employee_id в App.tsx/TopNav).
  my_games: () => true,
  directory: (p) => p.pages.personnel,
  branches: (p) => p.pages.personnel,  // подключение филиалов — управленцам (то же право, что «Персонал»)
  profile: () => true,                 // свой профиль доступен всем
  access: (p) => p.pages.personnel,
  roles_matrix: (p) => p.pages.personnel,  // вход в раздел — только супер-админ (гейт в компоненте)
  prebooking: (p) => p.pages.personnel,    // настройки предброни — руководитель+супер-админ (реальный гейт в App.tsx по access_role)
  pay_rates: (p) => p.pages.pay_rates,     // справочник ставок — финансист/руководитель/супер-админ (реальный гейт в компоненте)
};
function pageAllowed(user: CurrentUser | null, page: Page): boolean {
  return PAGE_PERM[page](perms(user));
}
/** Человекочитаемая строка из любой ошибки (никогда не «[object Object]»). */
export function errText(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object') {
    const o = e as any;
    if (typeof o.message === 'string') return o.message;
    if (typeof o.error === 'string') return o.error;
    try { return JSON.stringify(o); } catch { return 'Неизвестная ошибка'; }
  }
  return String(e ?? 'Неизвестная ошибка');
}
function firstAllowedPage(user: CurrentUser | null): Page {
  const order: Page[] = ['calendar', 'dashboard', 'crm', 'personnel', 'finance', 'directory'];
  return order.find((p) => pageAllowed(user, p)) ?? 'calendar';
}

export const useStore = create<State>((set, get) => ({
  branches: [],
  employees: [],
  teams: [],
  sources: [],
  occasions: [],
  game_formats: [],
  refusal_reasons: [],
  deals: [],
  dealAttachments: {},
  settings: DEFAULT_SETTINGS,
  currencyRates: [],
  ready: false,
  dealsLoadingMore: false,

  currentUser: null,
  authStatus: 'loading',
  authError: null,

  bitrixConnected: false,
  bitrixUser: null,
  bitrixError: null,
  bitrixDealId: null,

  page: 'calendar',
  salesDealId: null,
  dealNavIds: [],
  personnelTab: 'employees',
  modal: null,
  toasts: [],
  attachmentSaving: 0,
  // Последний выбранный филиал запоминаем в браузере (как валюту на дашборде), чтобы
  // после F5 он не сбрасывался на «Все филиалы». Проверку доступа делаем в loadData:
  // если города у человека больше нет — там откатим на 'all'.
  filterBranchId: ((): ID | 'all' => {
    try { return (localStorage.getItem('filter_branch') as ID | 'all') || 'all'; } catch { return 'all'; }
  })(),
  filterTeamId: 'all',
  markerFilter: 'none',
  filterDept: 'all',
  filterStatus: 'all',
  search: '',
  clockTz: { kind: 'system' },
  pendingCalView: null,
  pendingCalDay: null,

  async init() {
    // Если сервер ответит «вход протух» (401) — разлогиниваем и показываем вход.
    setUnauthorizedHandler(() => {
      setToken(null);
      set({
        currentUser: null, authStatus: 'anonymous', ready: false,
        branches: [], employees: [], teams: [], sources: [], occasions: [],
        game_formats: [], refusal_reasons: [], deals: [],
        bitrixConnected: false, bitrixUser: null,
      });
    });

    repo = await getRepo();

    // Локальный режим (без backend) — авторизация не нужна, синтетический владелец.
    if (repo.mode === 'local') {
      set({ currentUser: LOCAL_DEV_USER, authStatus: 'authed' });
      await get().loadData();
      return;
    }

    // API-режим: опознаём по сохранённому токену, иначе — авто-вход из Битрикса.
    set({ authStatus: 'loading' });
    let me = await fetchMe();
    if (!me && isInBitrix()) {
      try {
        await initBitrix();
        const auth = getBitrixAuth();
        if (auth) me = await apiLoginBitrix(auth.domain, auth.access_token);
      } catch (e) {
        set({ authError: errText(e) });
      }
    }

    if (me) {
      set({ currentUser: me, authStatus: 'authed', authError: null });
      await get().loadData();
    } else {
      // Показать экран входа (standalone) — данные не грузим до авторизации.
      set({ authStatus: 'anonymous', ready: true });
    }
  },

  async loadData() {
    const s = await repo.getSettings();
    set({ settings: s });
    // Курсы валют — необязательный справочник: если роута ещё нет или он упал, просто
    // работаем без пересчёта (суммы показываем как раньше), пользователю ничего не говорим.
    void fetchCurrencyRates().then((rates) => set({ currencyRates: rates }));

    // ── Двухфазная загрузка сделок ─────────────────────────────
    // ФАЗА 1 (быстро): справочники + сделки только за текущий месяц (с запасом ±7 дней),
    // чтобы первый экран отрисовался, не дожидаясь всех ~4500 игр с фото (это ~8 сек / 1.3 ГБ).
    const DAY = 86_400_000;
    const now = new Date();
    const from = new Date(now.getFullYear(), now.getMonth(), 1).getTime() - 7 * DAY;
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 1).getTime() + 7 * DAY;
    const [refs, monthDeals] = await Promise.all([repo.loadRefs(), repo.loadDeals(from, to)]);
    set({ ...refs, deals: monthDeals });

    // ФАЗА 2 (фон): дотягиваем ВСЕ сделки и заменяем массив целиком (без дублей).
    // Ошибку не эскалируем — остаёмся на месячных данных, просто снимаем флаг.
    set({ dealsLoadingMore: true });
    void repo.loadDeals()
      .then((allDeals) => set({ deals: allDeals, dealsLoadingMore: false }))
      .catch((e) => {
        console.warn('[deals] фоновая догрузка всех сделок не удалась:', errText(e));
        set({ dealsLoadingMore: false });
      });

    const u = get().currentUser;
    // Привязка филиала к правам пользователя — ГЛОБАЛЬНО (не только на Дашборде), чтобы
    // ограниченный пользователь (напр. исполнитель), попадающий сразу на Календарь, видел в
    // боковой панели «Команды» только свой филиал. Повторяет модель из DashboardPage, но здесь,
    // в сторе, срабатывает при любой посадочной странице.
    const perms = u?.permissions;
    const canSwitch = perms?.citySwitcher ?? false;
    const myCities = u?.city_ids ?? [];
    // Города из карточки сужают выбор ЛЮБОЙ роли; если их нет — только тем, кто может переключать.
    const allowedBranches = myCities.length > 0
      ? get().branches.filter((b) => myCities.includes(b.id))
      : (canSwitch ? get().branches : []);
    const fb = get().filterBranchId;
    if (allowedBranches.length === 1) {
      // Один доступный филиал → жёстко его (исполнитель/управляющий одного города).
      if (fb !== allowedBranches[0].id) get().setFilterBranch(allowedBranches[0].id);
    } else if (allowedBranches.length > 1) {
      // Мультигород: разрешаем «все мои», но не чужой/исчезнувший филиал.
      if (fb !== 'all' && !allowedBranches.some((b) => b.id === fb)) get().setFilterBranch(allowedBranches[0].id);
    }
    // allowedBranches.length === 0 (супер-админ/owner без заданных city_ids — видят всё):
    // filterBranchId не трогаем, оставляем сохранённый в localStorage/'all'.
    // Посадочная: если текущая страница недоступна роли — открыть первую разрешённую.
    set({ ready: true, page: pageAllowed(u, get().page) ? get().page : firstAllowedPage(u) });
    // Во встроенном режиме — подтянуть управленцев из Битрикса (для ответственных)
    // и определить, не открыты ли мы из карточки сделки (placement).
    if (isInBitrix()) { void get().connectBitrix(); void get().detectBitrixPlacement(); }
  },

  async detectBitrixPlacement() {
    try {
      await initBitrix();
      const rawId = getPlacementDealId();
      set({ bitrixDealId: rawId ? `bx${rawId}` : null });
    } catch {
      // не во встроенном режиме / нет контекста — игнорируем
    }
  },

  clearBitrixDeal() { set({ bitrixDealId: null }); },

  async login(login, password) {
    set({ authError: null });
    try {
      const user = await loginLocal(login, password);
      set({ currentUser: user, authStatus: 'authed', authError: null, ready: false });
      await get().loadData();
    } catch (e) {
      const msg = errText(e);
      set({ authError: msg });
      throw e;
    }
  },

  async loginViaBitrix() {
    set({ authError: null });
    try {
      await initBitrix();
      const auth = getBitrixAuth();
      if (!auth) throw new Error('Не удалось получить данные Битрикса');
      const user = await apiLoginBitrix(auth.domain, auth.access_token);
      set({ currentUser: user, authStatus: 'authed', authError: null, ready: false });
      await get().loadData();
    } catch (e) {
      const msg = errText(e);
      set({ authError: msg });
      throw e;
    }
  },

  async register(token, login, password, fullName, phone, branchId, workerType) {
    set({ authError: null });
    try {
      const res = await apiRegister(token, login, password, fullName, phone, branchId, workerType);
      // Общая ссылка → ждёт подтверждения: НЕ логиним, экран покажет «заявка отправлена».
      if (res.pending) return true;
      set({ currentUser: res.user, authStatus: 'authed', authError: null, ready: false });
      await get().loadData();
      return false;
    } catch (e) {
      const msg = errText(e);
      set({ authError: msg });
      throw e;
    }
  },

  async updateMyProfile(email, phone, login) {
    // login присылаем только когда его реально меняют (вход по логину) — иначе ключа
    // нет в patch и сервер его не трогает. Для Битрикс-входа login не передаём вовсе.
    const patch: { email: string | null; phone: string | null; login?: string } =
      login !== undefined ? { email, phone, login } : { email, phone };
    const user = await apiUpdateProfile(patch);
    set({ currentUser: user });
    await get().reload(); // подтянуть обновлённый телефон из карточки сотрудника
  },

  async updateMyAvatar(avatar) {
    const user = await apiUpdateProfile({ avatar });
    set({ currentUser: user });
  },

  async updateMyBirthDate(birthDate) {
    const user = await apiUpdateProfile({ birth_date: birthDate });
    set({ currentUser: user });
    await get().reload(); // подтянуть birth_date в карточку сотрудника (список employees)
  },

  async requestTelegramLink() {
    const { deep_link } = await apiRequestTelegramLink();
    return deep_link;
  },

  async unlinkTelegram() {
    await apiUnlinkTelegram();
    await get().refreshMe(); // перезапросить /me — подтянуть сброшенный статус привязки в currentUser
  },

  async refreshMe() {
    const me = await fetchMe();
    if (me) set({ currentUser: me });
  },

  async logout() {
    await apiLogout();
    set({
      currentUser: null,
      authStatus: 'anonymous',
      ready: false,
      // чистим кэши данных
      branches: [], employees: [], teams: [], sources: [], occasions: [],
      game_formats: [], refusal_reasons: [], deals: [],
      bitrixConnected: false, bitrixUser: null,
    });
  },

  async connectBitrix() {
    if (!isInBitrix()) {
      set({ bitrixError: 'Приложение открыто не внутри Битрикса' });
      return;
    }
    try {
      await initBitrix();
      // Сотрудников НЕ подменяем: они живут в нашей БД (линейный персонал заводится в ЕРП,
      // управленцы приезжают синхрой как bxu-*, с филиалом и должностью). Раньше здесь
      // грузился user.get и затирал список — во встроенном режиме экран «Персонал»
      // показывал всех пользователей портала без филиалов.
      const user = await fetchCurrentUser().catch(() => null);
      set({ bitrixConnected: true, bitrixUser: user?.name ?? null, bitrixError: null });
    } catch (e) {
      const msg = errText(e);
      set({ bitrixConnected: false, bitrixError: msg });
    }
  },

  async reload() {
    const snap = await repo.loadAll();
    set(snap);
  },

  setPage(p) {
    const u = get().currentUser;
    set({ page: pageAllowed(u, p) ? p : firstAllowedPage(u) });
  },
  // Открытие карточки всегда переустанавливает список соседей: не передали — листания нет
  // (иначе от прошлого экрана остался бы чужой список).
  openSalesDeal(id, navIds) { set({ salesDealId: id, dealNavIds: navIds ?? [] }); },
  setPersonnelTab(t) { set({ personnelTab: t }); },
  setModal(m, navIds) { set({ modal: m, dealNavIds: navIds ?? [] }); },
  navigateDeal(id) {
    const s = get();
    if (s.salesDealId !== null) set({ salesDealId: id });
    else if (s.modal?.kind === 'deal') set({ modal: { kind: 'deal', id } });
  },
  toast(t) {
    const id = nanoid();
    set((s) => ({ toasts: [...s.toasts, { id, ...t }] }));
    setTimeout(() => get().dismissToast(id), 4000);
  },
  dismissToast(id) { set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })); },

  setFilterBranch(id) {
    set({ filterBranchId: id });
    try { localStorage.setItem('filter_branch', String(id)); } catch { /* приватный режим — просто не запоминаем */ }
  },
  setPendingCal(view, day = null) { set({ pendingCalView: view, pendingCalDay: day }); },
  setMarkerFilter(m) { set({ markerFilter: m }); },
  setFilterTeam(id) { set({ filterTeamId: id }); },
  setFilterDept(d) { set({ filterDept: d }); },
  setFilterStatus(s) { set({ filterStatus: s }); },
  setSearch(s) { set({ search: s }); },
  setClockTz(tz) { set({ clockTz: tz }); },

  async patchSettings(p) {
    const next = { ...get().settings, ...p };
    await repo.putSettings(next);
    set({ settings: next });
  },

  async saveBranch(b) { await repo.putBranch(b); await get().reload(); },
  async deleteBranch(id) { await repo.deleteBranch(id); await get().reload(); },
  async saveEmployee(e) { await repo.putEmployee(e); await get().reload(); },
  async deleteEmployee(id) { await repo.deleteEmployee(id); await get().reload(); },
  async saveTeam(t) { await repo.putTeam(t); await get().reload(); },
  async deleteTeam(id) { await repo.deleteTeam(id); await get().reload(); },

  async saveDeal(d) {
    const next = { ...d, updated_at: Date.now() };
    // Оптимистично: сразу кладём сделку в стор, чтобы интерфейс перерисовался
    // мгновенно (карточка на ленте «Ресурсы» двигается без ожидания сети),
    // а PUT уходит в фоне. `prevDeal` — версия ДО правки для отката при ошибке.
    const prevDeal = get().deals.find((x) => x.id === next.id) ?? null;
    const exists = prevDeal != null;
    set((s) => ({
      deals: exists ? s.deals.map((x) => (x.id === next.id ? next : x)) : [next, ...s.deals],
    }));
    // Есть ли в сохраняемых данных РЕАЛЬНОЕ тело вложения (свежеприкреплённый/загруженный
    // файл), которое уйдёт на сервер? Если да — на время PUT поднимаем счётчик активных
    // сохранений с вложением: пока он > 0, показываем плашку «не закрывайте страницу» и
    // предупреждаем перед перезагрузкой (на мобильном PUT с фото идёт несколько секунд, и
    // пользователи успевали обновить страницу — отправка обрывалась, файл не сохранялся).
    const hasAttachmentBody = hasUnsavedAttachmentBody(next);
    if (hasAttachmentBody) set((s) => ({ attachmentSaving: s.attachmentSaving + 1 }));
    try {
      // PUT возвращает сделку, пересчитанную сервером (напр. prebooking_expires_at
      // при смене типа клиента) — вмерживаем именно её, чтобы не потерять пересчёт.
      const saved = await repo.putDeal(next);
      // Тел вложений в ответе нет (сервер их не отдаёт), поэтому подставляем те, что есть у
      // нас: только что прикреплённый файл + всё, что успело догрузиться. Иначе фото на глазах
      // исчезло бы из открытой карточки сразу после сохранения.
      const bodies: AttachmentBodies = {
        ...(get().dealAttachments[saved.id] ?? {}),
        ...collectAttachmentBodies(next),
      };
      set((s) => ({
        dealAttachments: { ...s.dealAttachments, [saved.id]: bodies },
        deals: s.deals.map((x) => (x.id === saved.id ? applyAttachmentBodies(saved, bodies) : x)),
      }));
    } catch (e) {
      // Откат к исходному состоянию именно этой сделки (не трогаем прочие,
      // которые могли измениться параллельно): вернуть прежнюю версию либо
      // убрать только что добавленную.
      set((s) => ({
        deals: prevDeal
          ? s.deals.map((x) => (x.id === next.id ? prevDeal : x))
          : s.deals.filter((x) => x.id !== next.id),
      }));
      get().toast({ kind: 'error', text: errText(e) });
      throw e;
    } finally {
      // Снимаем счётчик и при успехе, и при ошибке — плашка не должна залипать.
      if (hasAttachmentBody) set((s) => ({ attachmentSaving: Math.max(0, s.attachmentSaving - 1) }));
    }
  },

  // Фоновая догрузка вложений открытой карточки. Грузим ПО ОДНОМУ (следующий файл — только
  // после предыдущего), чтобы десяток фото по 4 МБ не подвешивал интерфейс. Уже скачанное
  // лежит в кэше dealAttachments и повторно не качается.
  async loadDealAttachments(id) {
    const cached = get().dealAttachments[id];
    // Сначала — то, что уже в кэше (после reload сделка снова приходит без тел).
    if (cached) set((s) => ({ deals: s.deals.map((x) => (x.id === id ? applyAttachmentBodies(x, cached) : x)) }));
    const deal = get().deals.find((x) => x.id === id);
    if (!deal) return;
    const keys = pendingAttachmentKeys(deal);
    if (keys.length === 0) return;

    const run = ++attachRun;
    for (const key of keys) {
      if (run !== attachRun) return;   // карточку закрыли/пролистали — хвост не нужен
      try {
        const data_url = await repo.getDealAttachment(id, key);
        if (run !== attachRun) return;
        if (!data_url) continue;
        set((s) => {
          const bodies = { ...(s.dealAttachments[id] ?? {}), [key]: data_url };
          return {
            dealAttachments: { ...s.dealAttachments, [id]: bodies },
            deals: s.deals.map((x) => (x.id === id ? applyAttachmentBodies(x, bodies) : x)),
          };
        });
      } catch {
        // Молча: не догрузили одно вложение (сеть/закрыли карточку) — не повод шуметь
        // ошибкой на весь экран, на его месте просто останется заглушка.
      }
    }
  },

  cancelDealAttachments() { attachRun++; },

  async linkDeal(erpId, opts) {
    const res = await linkDealToBitrix(erpId, opts);
    if (res.ok) await get().reload(); // подтянуть привязанное событие с данными сделки
    return res;
  },

  async newDeal(kind, preset) {
    const number = computeNextDealNumber(get().deals, kind);
    const now = Date.now();
    // Город по умолчанию — ТОТ, что сейчас выбран в шапке (filterBranchId), иначе свой
    // город создателя (если он привязан к одному городу), иначе общий дефолт. Раньше при
    // нескольких доступных городах (filterBranchId не учитывался вовсе) сделка всегда
    // уезжала в один и тот же запасной филиал независимо от того, какой город выбран —
    // человек с 2+ городами создавал игру, глядя на Шымкент, а она попадала в Ташкент.
    const filterBranchId = get().filterBranchId;
    const ownCities = get().currentUser?.city_ids ?? [];
    const defaultBranch = (filterBranchId !== 'all' ? filterBranchId : null)
      ?? (ownCities.length === 1 ? ownCities[0] : null)
      ?? get().settings.default_branch_id ?? get().branches[0]?.id ?? null;
    const draft: Deal = {
      id: nanoid(),
      number,
      kind,
      currency: get().settings.default_currency,
      contact_name: '',
      branch_id: defaultBranch,
      status: 'new',
      created_at: now,
      taken_at: now,
      payment: { prepayment_receipts: [], remainder_receipts: [] },
      event: {},
      transactions: [],
      staff_assignments: [],
      history: [{ ts: now, action: 'Создано' }],
      updated_at: now,
      // Агентская продажа — по умолчанию ДА для НОВЫХ игр (решение заказчика 02.11.2026:
      // такие продажи у них частые, проще снять галочку вручную, чем каждый раз ставить).
      // Старые игры без этого поля — НЕ трогаем, расчёт зарплаты за прошлое не пересчитывается.
      agent_sale: true,
      ...preset,
    };
    // Добавляем сделку точечно (оптимистично + мерж ответа сервера), без полной
    // перезагрузки всех сделок — новая карточка появляется сразу.
    await get().saveDeal(draft);
    return draft;
  },

  async deleteDeal(id) {
    const prev = get().deals;
    // Убираем из стора сразу; при ошибке — возвращаем исходный список.
    set((s) => ({ deals: s.deals.filter((x) => x.id !== id) }));
    try {
      await repo.deleteDeal(id);
    } catch (e) {
      set({ deals: prev });
      get().toast({ kind: 'error', text: errText(e) });
      throw e;
    }
  },

  async duplicateDeal(id) {
    const orig = get().deals.find((d) => d.id === id);
    if (!orig) return null;
    const number = computeNextDealNumber(get().deals, orig.kind);
    const now = Date.now();
    const dup: Deal = {
      ...structuredClone(orig),
      id: nanoid(),
      number,
      bitrix_id: undefined,
      bitrix_contact_id: undefined,
      bitrix_company_id: undefined,
      bitrix_calendar_event_id: undefined,
      sync_state: 'local',
      synced_at: undefined,
      sync_error: undefined,
      status: 'new',
      created_at: now,
      taken_at: now,
      updated_at: now,
    };
    // Новая сделка добавляется точечно (внутри saveDeal), без полной перезагрузки.
    await get().saveDeal(dup);
    get().toast({ kind: 'success', text: `Скопировано: ${dup.number}` });
    return dup;
  },

  async moveDealToDay(id, newDayTs, branchId) {
    const orig = get().deals.find((d) => d.id === id);
    if (!orig) return;
    const oldEventDay = orig.event.event_date != null ? startOfDay(orig.event.event_date) : null;
    const newDay = startOfDay(newDayTs);
    const delta = oldEventDay != null ? newDay - oldEventDay : 0;
    const next: Deal = {
      ...orig,
      branch_id: branchId !== undefined ? branchId : orig.branch_id,
      event: {
        ...orig.event,
        event_date: newDay,
        start_at: orig.event.start_at != null
          ? (oldEventDay != null ? orig.event.start_at + delta : newDay + 18 * 3600_000)
          : newDay + 18 * 3600_000,
        end_at: orig.event.end_at != null
          ? (oldEventDay != null ? orig.event.end_at + delta : newDay + 20 * 3600_000)
          : newDay + 20 * 3600_000,
      },
      updated_at: Date.now(),
    };
    // Точечное сохранение (оптимистично + мерж ответа сервера), без reload().
    await get().saveDeal(next);
  },

  async unscheduleDeal(id) {
    const orig = get().deals.find((d) => d.id === id);
    if (!orig) return;
    const next: Deal = {
      ...orig,
      event: {
        ...orig.event,
        event_date: undefined,
        start_at: undefined,
        end_at: undefined,
      },
      updated_at: Date.now(),
    };
    // Точечное сохранение без reload(); тост об успехе — только если сохранилось.
    await get().saveDeal(next);
    get().toast({ kind: 'info', text: `${orig.number} → возвращена в очередь` });
  },

  async setDealStatus(id, status) {
    const orig = get().deals.find((d) => d.id === id);
    if (!orig) return;
    const next: Deal = {
      ...orig,
      status,
      taken_at: orig.taken_at ?? Date.now(),
      updated_at: Date.now(),
    };
    // Точечное сохранение (оптимистично + мерж ответа сервера), без reload().
    await get().saveDeal(next);
  },
}));

function startOfDay(d: number): number { return soDay(d); }
